import { Image } from "@tiptap/extension-image";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table";
import { Markdown } from "@tiptap/markdown";
import type { EditorView } from "@tiptap/pm/view";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BlockSuggestionMenu,
  type MentionSources,
  MentionSuggestionMenu,
} from "~/features/documents/editor/EditorSuggestions";
import { EditorToolbar, type EditorToolbarAi } from "~/features/documents/editor/EditorToolbar";
import {
  type ConversationClick,
  ConversationNode,
} from "~/features/documents/editor/extensions/conversation";
import {
  InstructionBlockNode,
  type InstructionBlockOptions,
} from "~/features/documents/editor/extensions/instructionBlock";
import { DocitMarkdownRules } from "~/features/documents/editor/extensions/markdownRules";
import { MentionNode } from "~/features/documents/editor/extensions/mention";
import { PartIdentity } from "~/features/documents/editor/extensions/partId";
import { ChartNode, WidgetNode } from "~/features/documents/editor/extensions/widget";
import {
  createSuggestionExtension,
  EMPTY_SUGGESTION,
  type SuggestionState,
} from "~/features/documents/editor/suggestionTrigger";

export interface DocumentEditorProps {
  /** The markdown source of truth — what the platform's `text` field carries. */
  markdown: string;
  editable?: boolean;
  /** Fires with the full markdown after every change. */
  onChange?: (markdown: string) => void;
  withToolbar?: boolean;
  autoFocus?: boolean;
  className?: string;
  ariaLabel?: string;
  /**
   * The in-editor AI segment (W6). Supplied only where the host has a document to act on and
   * a viewer allowed to act — the affordance is gated on the update ability, because
   * `transform-selection` itself performs NO document authorization and must not be widened
   * into "transform any text you can post".
   */
  ai?: EditorToolbarAi;
  /** Hands the live editor back to the host, so it can run selection actions against it. */
  onEditorReady?: (editor: ReturnType<typeof useEditor>) => void;
  /**
   * Clicking an embedded conversation card. Creating one belongs to a later wave, so a host
   * that has nowhere to send this should say so rather than open nothing.
   */
  onOpenConversation?: (info: ConversationClick) => void;
  /** Apply / retry on an instruction card. */
  instructions?: InstructionBlockOptions;
  /**
   * Who the `@` menu may offer. Omit it and the mention menu does not open at all — better
   * than an empty list that looks like nobody exists.
   */
  mentions?: MentionSources;
  /**
   * W10 §10 — how a chosen image becomes a URL.
   *
   * The editor cannot upload: an upload needs the document's id and an API client, and this
   * component is given neither. So the host supplies one function, and without it the image
   * affordances stay off entirely — a picker that produced a `blob:` URL would look like it
   * worked and lose the picture on the next reload.
   */
  onUploadImage?: (file: File) => Promise<string>;
}

/** Only real image files — a pasted screenshot is a `File` on the clipboard, not text. */
function imageFilesOf(data: DataTransfer | null): File[] {
  return Array.from(data?.files ?? []).filter((file) => file.type.startsWith("image/"));
}

/**
 * Upload each image and insert it where it landed. Sequential on purpose: two parallel uploads
 * inserting at the same recorded position would put the second one inside the first.
 */
async function insertUploadedImages(
  view: EditorView,
  files: File[],
  upload: (file: File) => Promise<string>,
  at: number | null,
): Promise<void> {
  for (const file of files) {
    try {
      const src = await upload(file);
      const node = view.state.schema.nodes.image?.create({ src, alt: file.name });
      if (!node) {
        return;
      }
      const position = at ?? view.state.selection.from;
      view.dispatch(view.state.tr.insert(position, node));
    } catch {
      // The host toasts the reason; inserting a broken image would be worse than inserting none.
      return;
    }
  }
}

/**
 * The document surface, read and write both — Tiptap 3 (ProseMirror) rendering markdown.
 * Markdown in, markdown out: the platform's canonical `text` projection is the interchange,
 * so nothing here ever touches ProseMirror JSON or collaboration state.
 */
export function DocumentEditor({
  markdown,
  editable = false,
  onChange,
  withToolbar = false,
  autoFocus = false,
  className,
  ariaLabel,
  ai,
  onEditorReady,
  onOpenConversation,
  instructions,
  mentions,
  onUploadImage,
}: DocumentEditorProps) {
  const [slash, setSlash] = useState<SuggestionState>(EMPTY_SUGGESTION);
  const [at, setAt] = useState<SuggestionState>(EMPTY_SUGGESTION);
  const fileInput = useRef<HTMLInputElement | null>(null);
  /**
   * The upload function, held in a ref so the editor's `editorProps` (built once, in a memo)
   * always reach the CURRENT one. Putting `onUploadImage` in the extensions memo instead would
   * rebuild the whole schema whenever the host re-rendered.
   */
  const uploadRef = useRef(onUploadImage);
  uploadRef.current = onUploadImage;
  /**
   * The owner's own block nodes, on top of the markdown lane.
   *
   * `PartIdentity` goes LAST so its global attribute reaches every type registered above it —
   * including the four added here, which are as much "parts" of a document as a paragraph is.
   */
  const extensions = useMemo(
    () => [
      StarterKit,
      Markdown,
      /**
       * W10 §10 — three MIT Tiptap blocks. All three round-trip through `@tiptap/markdown`
       * (asserted in `extensions/blocks.test.ts`), which is the only reason they are here:
       * markdown is the interchange, and a block that cannot be serialised would be silently
       * destroyed on the next save.
       *
       * The table node and its three cell/row nodes are listed one by one rather than through
       * `TableKit`: the kit already registers all four itself, so naming them alongside it
       * registered each one TWICE ("Duplicate extension names" in the console). Listing them
       * is still the point — nothing can silently drop one — the kit is just not the way to do
       * it. The list package contributes ONLY `TaskList`/`TaskItem` — `StarterKit` already owns
       * the bullet and ordered lists, and registering them twice is a schema conflict.
       */
      Table,
      TableRow,
      TableHeader,
      TableCell,
      TaskList,
      // `nested` so a task can hold a sub-task; the markdown serialiser indents them.
      TaskItem.configure({ nested: true }),
      // No `allowBase64`: a data: URI in a document body is a megabyte of markdown that no
      // search index or export can do anything with. Images become attachments or nothing.
      Image,
      // The parse rules come FIRST and at high priority — they must be tried before the
      // ordinary code block and paragraph, without reordering the schema (see markdownRules).
      ...DocitMarkdownRules,
      WidgetNode,
      ChartNode,
      ConversationNode.configure({
        ...(onOpenConversation ? { onOpenConversation } : {}),
        readOnly: !editable,
      }),
      InstructionBlockNode.configure(instructions ?? {}),
      MentionNode,
      // Triggers only while editing: a reader typing "/" is reading, not inserting.
      ...(editable
        ? [
            createSuggestionExtension({ char: "/", allowSpaces: false, onChange: setSlash }),
            createSuggestionExtension({ char: "@", allowSpaces: false, onChange: setAt }),
          ]
        : []),
      PartIdentity,
    ],
    [editable, instructions, onOpenConversation],
  );

  const editor = useEditor({
    extensions,
    content: markdown,
    contentType: "markdown",
    editable,
    autofocus: autoFocus ? "end" : false,
    onUpdate: ({ editor: current }) => onChange?.(current.getMarkdown()),
    editorProps: {
      attributes: {
        class: "slate-prose",
        ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
      },
      /**
       * W10 §10 — pasting or dropping a picture is how images really arrive.
       *
       * Both handlers return `false` when there is no upload seam or no image in the payload,
       * which hands the event straight back to ProseMirror — so pasting text still behaves
       * exactly as it did. The upload is fired and the node inserted when it resolves; a
       * failure inserts nothing rather than a broken image.
       */
      handlePaste: (view, event) => {
        const files = imageFilesOf(event.clipboardData);
        if (!editable || files.length === 0 || !uploadRef.current) {
          return false;
        }
        event.preventDefault();
        void insertUploadedImages(view, files, uploadRef.current, null);
        return true;
      },
      handleDrop: (view, event) => {
        const files = imageFilesOf((event as DragEvent).dataTransfer);
        if (!editable || files.length === 0 || !uploadRef.current) {
          return false;
        }
        event.preventDefault();
        const at = view.posAtCoords({
          left: (event as DragEvent).clientX,
          top: (event as DragEvent).clientY,
        });
        void insertUploadedImages(view, files, uploadRef.current, at?.pos ?? null);
        return true;
      },
    },
  });

  /** The `/image` entry's file picker — one hidden input, reused. */
  const pickImage = useCallback(() => fileInput.current?.click(), []);

  useEffect(() => {
    onEditorReady?.(editor);
  }, [editor, onEditorReady]);

  // A read-only render follows external changes (a sculpt applied, a refetch). An editable
  // editor owns its draft and must never be clobbered from outside mid-typing.
  useEffect(() => {
    if (!editable && editor && markdown !== editor.getMarkdown()) {
      editor.commands.setContent(markdown, { contentType: "markdown" });
    }
  }, [editable, editor, markdown]);

  return (
    <div className={className}>
      {withToolbar && editable && editor ? (
        <EditorToolbar editor={editor} {...(ai ? { ai } : {})} />
      ) : null}
      <EditorContent editor={editor} />
      {editable && editor ? (
        <BlockSuggestionMenu
          editor={editor}
          state={slash}
          onDismiss={() => setSlash(EMPTY_SUGGESTION)}
          {...(onUploadImage ? { onInsertImage: pickImage } : {})}
        />
      ) : null}
      {editable && onUploadImage ? (
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          hidden
          onChange={async (event) => {
            const file = event.target.files?.[0];
            // Reset first: choosing the SAME file twice must fire `change` both times.
            event.target.value = "";
            if (!file || !editor) {
              return;
            }
            const url = await onUploadImage(file);
            editor.chain().focus().setImage({ src: url, alt: file.name }).run();
          }}
        />
      ) : null}
      {editable && editor && mentions ? (
        <MentionSuggestionMenu
          editor={editor}
          state={at}
          sources={mentions}
          onDismiss={() => setAt(EMPTY_SUGGESTION)}
        />
      ) : null}
    </div>
  );
}
